import {useFormik} from 'formik'
import React, {useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import Container from 'react-bootstrap/Container'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import * as Yup from 'yup'
import {useA1BatchesProvider} from '../../../../../../gori/providers'
import {Button, UploadFile, ValidationErrorModal} from '../../../../../../gori/widgets'
import {A1BatchService, A1_CONSTANTS, T86DocumentTable} from '../../../index'
import {useCancelToken, UsePermission} from '../../../../../../gori/hooks'
import {useParams} from 'react-router-dom'

type Props = {
  show: boolean
  handleClose: () => void
}

const T86DocumentModal: React.FC<Props> = ({show, handleClose}) => {
  const intl = useIntl()
  const {id} = useParams()
  const {a1Batch} = useA1BatchesProvider()
  const {isCancel, newCancelToken} = useCancelToken()
  const [validationErrors, setValidationErrors] = useState<any>()
  const [reloadForm, setReloadForm] = useState<boolean>(false)
  const {isOrigin} = UsePermission()

  const [loading, setLoading] = useState<{
    list: boolean
    update: boolean
    download: boolean
  }>({
    list: false,
    update: false,
    download: false,
  })

  const {PICK_UP_DROP_OFF} = A1_CONSTANTS.OPTIONS_JOURNEY_STATUS

  const canEdit = useMemo(() => {
    return isOrigin && PICK_UP_DROP_OFF.value[2] === a1Batch?.journey_status
  }, [isOrigin, PICK_UP_DROP_OFF, a1Batch?.journey_status])

  // Yamato batches do not require T86 documents.
  const isYamato = useMemo(() => !!a1Batch?.is_yamato, [a1Batch?.is_yamato])

  const handleCloseModal = () => {
    handleClose()
  }

  const initValidateSchema = useMemo(() => {
    return {
      files: isYamato ? Yup.array() : Yup.array().min(1, intl.formatMessage({id: 'REQUIRED'})),
    }
  }, [intl, isYamato])

  const formik = useFormik({
    initialValues: {
      files: [],
    },
    validationSchema: Yup.object().shape(initValidateSchema),
    onSubmit: async (values) => {
      try {
        setReloadForm(false)
        setLoading((prev) => ({...prev, update: true}))
        const config = {cancelToken: newCancelToken()}
        const payload = new FormData()
        // @ts-ignore
        payload.append('batch_id', id)
        const files = values?.files
        files.map((file) => {
          payload.append('files[]', file)
          return file
        })
        const res: any = await A1BatchService.uploadT86Documents(a1Batch.id, payload, config)
        if (res) {
          toast.success(res.message)
          formik.resetForm()
          setReloadForm(true)
        }
      } catch (error: any) {
        if (isCancel(error)) return
        setValidationErrors(error?.response)
      } finally {
        setLoading((prev) => ({...prev, update: false}))
      }
    },
  })

  return (
    <>
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => {
            setValidationErrors(undefined)
          }}
          response={validationErrors}
        />
      )}
      <Modal
        id='gori_modal_upload_t86_documents_modal'
        tabIndex={-1}
        aria-hidden='true'
        centered
        dialogClassName='mw-1250px h-auto'
        show={show}
        backdrop='static'
        onHide={handleCloseModal}
      >
        <div className='modal-content'>
          <Modal.Header closeButton>
            <Modal.Title bsPrefix={'fw-bolder fs-1'}>
              {intl.formatMessage({id: 'DOCUMENTS'})}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className='vh-75 scroll-y'>
            <Container>
              {canEdit && (
                <div className='mt-10 upload-card'>
                  <div className='d-flex align-items-center justify-content-between'>
                    <h3 className={isYamato ? '' : 'required'}>
                      {intl.formatMessage({id: 'DOCUMENTS'})}
                    </h3>
                  </div>
                  <UploadFile
                    containerClassName='px-8 py-6 shadow-sm rounded-4 bg-white my-4'
                    className='border-primary border-2'
                    onSubmit={(e) => formik.setFieldValue('files', e.files)}
                    supportedFormat={Object.values(A1_CONSTANTS.UPLOAD_CONFIG.SUPPORTED_FORMATS)}
                    size={{label: '8MB', value: A1_CONSTANTS.UPLOAD_CONFIG.SIZE}}
                    reloadForm={reloadForm}
                    multiple={true}
                  />
                  {formik.errors.files && formik.touched.files && (
                    <div className='text-danger mt-2'>{formik.errors.files}</div>
                  )}
                </div>
              )}
              <T86DocumentTable
                reloadTable={reloadForm}
                height={canEdit ? '35vh' : '50vh'}
                emptyMessage={canEdit ? '' : intl.formatMessage({id: 'NO_DOCUMENTS_TO_VIEW'})}
              />
            </Container>
          </Modal.Body>
          <Modal.Footer>
            <div className='d-flex justify-content-end'>
              <Button
                className='btn btn-secondary me-2'
                label={intl.formatMessage({id: 'CANCEL'})}
                event={handleCloseModal}
              />
              {canEdit && (
                <Button
                  className='btn btn-primary'
                  label={intl.formatMessage({id: 'UPDATE'})}
                  disabled={loading.update || !formik.values.files.length}
                  event={formik.handleSubmit}
                  loading={loading.update}
                />
              )}
            </div>
          </Modal.Footer>
        </div>
      </Modal>
    </>
  )
}

export {T86DocumentModal}
